import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Card, List, Tag, Button, Space, Spin, Empty, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import employeeDocumentFolderService from "Services/EmployeeDocumentFolderService";
import API from "api/endpoints";
import { downloadFile } from "Common/downloadFile";

const { Text } = Typography;

// Maps a file role from the folder response to its backend download segment +
// an admin-friendly label. Note: the response uses "HrFinalVerified" but the
// download endpoint segment is "HrFinal".
const FILE_ROLES = {
  HrTemplate: { segment: "HrTemplate", label: "HR Template" },
  ApplicantSubmitted: { segment: "ApplicantSubmitted", label: "Applicant File" },
  HrFinalVerified: { segment: "HrFinal", label: "Final Copy" },
};

// Shared employee document folder view (per Mudassir's Jun-17 handoff).
// Renders the nested folder: source groups (cards) -> documents (rows) -> files
// (one download button per available version). Used by both the Received
// Documents page and the Employee Detail Documents tab.
function EmployeeDocumentFolderView({ employeeId }) {
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await employeeDocumentFolderService.getByEmployee(employeeId);
      setFolder(data);
    } catch (e) {
      // 404 = no folder linked yet (expected before applicant->employee conversion).
      if (e.response?.status !== 404) {
        console.error("Error loading employee document folder:", e);
        toast.error("Failed to load the document folder");
      }
      setFolder(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId, load]);

  const handleDownload = async (doc, file) => {
    const role = FILE_ROLES[file.fileRole];
    if (!role) {
      toast.warning("This file cannot be downloaded");
      return;
    }
    const key = `${doc.sourceDocumentId}-${file.fileRole}`;
    setDownloadingKey(key);
    try {
      const url = API.hiringDocumentPackage.fileDownload(doc.sourceDocumentId, role.segment);
      const fileName = (file.filePath || "").split("/").pop() || doc.title || "document";
      await downloadFile(url, fileName);
    } catch (e) {
      console.error("Error downloading file:", e);
      toast.error("Failed to download the file");
    } finally {
      setDownloadingKey(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  const groups = folder?.sourceGroups || [];
  if (groups.length === 0) {
    return <Empty description="No documents have been linked for this employee yet." />;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {groups.map((group) => (
        <Card
          key={group.employeeDocumentSourceGroupId}
          title={group.title || "Documents"}
          size="small"
          type="inner"
        >
          {group.description && (
            <p style={{ color: "rgba(0,0,0,0.45)", marginBottom: 12 }}>{group.description}</p>
          )}
          <List
            size="small"
            dataSource={group.documents || []}
            locale={{ emptyText: "No documents in this group" }}
            renderItem={(doc) => (
              <List.Item key={doc.sourceDocumentId}>
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{doc.title || doc.documentType}</span>
                    {doc.status && <Tag style={{ marginRight: 0 }}>{doc.status}</Tag>}
                  </div>

                  {doc.description && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                        {doc.description}
                      </Text>
                    </div>
                  )}

                  {doc.inPersonVerified && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                        Verified in person
                        {doc.verifiedOn ? ` on ${new Date(doc.verifiedOn).toLocaleDateString()}` : ""}
                        {doc.verificationNotes ? ` - ${doc.verificationNotes}` : ""}
                      </Text>
                    </div>
                  )}

                  {doc.applicantAcknowledged && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                        Acknowledged
                        {doc.acknowledgedDocumentVersion
                          ? ` (v${doc.acknowledgedDocumentVersion})`
                          : ""}
                      </Text>
                    </div>
                  )}

                  <div style={{ marginTop: 8 }}>
                    {(doc.files || []).length === 0 ? (
                      <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                        No files
                      </Text>
                    ) : (
                      <Space wrap size="small">
                        {doc.files.map((file) => {
                          const role = FILE_ROLES[file.fileRole];
                          const key = `${doc.sourceDocumentId}-${file.fileRole}`;
                          return (
                            <div
                              key={file.fileRole}
                              style={{ display: "flex", flexDirection: "column", gap: 2 }}
                            >
                              <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                loading={downloadingKey === key}
                                onClick={() => handleDownload(doc, file)}
                              >
                                {role ? role.label : file.label || "File"}
                              </Button>
                              {file.uploadedOn && (
                                <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                                  Uploaded{" "}
                                  {new Date(file.uploadedOn).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </Text>
                              )}
                            </div>
                          );
                        })}
                      </Space>
                    )}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Card>
      ))}
    </Space>
  );
}

EmployeeDocumentFolderView.propTypes = {
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default EmployeeDocumentFolderView;
